// Herbal recipes & rituals \u2014 original content by the Herbs We Love team.
// Each recipe pairs one or more products with a simple, embodied practice.

export const recipes = [
  {
    id: 'golden-hour-morning-ritual',
    title: 'Golden Hour Morning Ritual',
    subtitle: 'Awaken with warmth and focus',
    time: '5 min',
    difficulty: 'Easy',
    moment: 'Morning',
    image: 'https://images.unsplash.com/photo-1519864600265-abb23847ef2c?w=1200&q=80&auto=format',
    intro: 'A grounded, sun-lit way to start your day \u2014 warming spices, a moment at the window, and a natural boost of clarity.',
    ingredients: [
      { text: '1 dropper of Fireball Elixir', productId: 'fireball-elixir' },
      { text: '1 cup of warm water or herbal tea' },
      { text: 'A slice of lemon (optional)' },
      { text: 'A quiet window with morning light' }
    ],
    steps: [
      'Fill your favorite cup with warm (not hot) water. Add lemon if you love it.',
      'Place one dropper of Fireball Elixir under your tongue. Hold it there for 30 seconds while breathing slowly.',
      'Sip your water slowly at the window. Notice three things you can see, hear and feel.',
      'Set one small intention for the day \u2014 gentle, doable, kind.'
    ],
    tip: 'Skip caffeine for the first hour if you can. The elixir\u2019s adaptogens work best on a settled stomach and calm nervous system.',
    relatedProducts: ['fireball-elixir', 'focus-capsules']
  },
  {
    id: 'twilight-wind-down',
    title: 'Twilight Wind-Down',
    subtitle: 'A soft, botanical descent into sleep',
    time: '30 min',
    difficulty: 'Easy',
    moment: 'Evening',
    image: 'https://images.unsplash.com/photo-1519681393784-d120267933ba?w=1200&q=80&auto=format',
    intro: 'When the sky turns violet, let your body follow. A slow, aromatic ritual designed to melt tension and welcome deep sleep.',
    ingredients: [
      { text: '1 tsp of Sleep Syrup with CBN', productId: 'sleep-syrup-w-cbn' },
      { text: 'A generous scoop of Lavender & Eucalyptus Sugar Scrub', productId: 'lavender-eucalyptus-sugar-body-scrub' },
      { text: 'A warm bath or shower' },
      { text: 'Cotton pyjamas and a dim lamp' }
    ],
    steps: [
      'Run a warm bath or shower. Turn off the overhead light and switch on a single dim lamp.',
      'Once you\u2019re in, massage the lavender scrub gently onto damp skin. Breathe in deeply for 4 counts, out for 6.',
      'Rinse, pat dry and change into something soft.',
      'Take 1 tsp of Sleep Syrup 30 minutes before bed. Sit quietly, phone away, letting the botanicals settle in.',
      'Slide under the covers with a gentle exhale.'
    ],
    tip: 'Keep phones out of the bedroom on ritual nights. The scent of lavender + the CBN in the syrup work most beautifully with true darkness.',
    relatedProducts: ['sleep-syrup-w-cbn', 'lavender-eucalyptus-sugar-body-scrub', 'somnific-capsules']
  },
  {
    id: 'the-focus-sit-down',
    title: 'The Focus Sit-Down',
    subtitle: 'Deep-work in 25 quiet minutes',
    time: '25 min',
    difficulty: 'Easy',
    moment: 'Midday',
    image: 'https://images.unsplash.com/photo-1544716278-e513176f20b5?w=1200&q=80&auto=format',
    intro: 'A short, sacred pocket of focus \u2014 supported by adaptogenic herbs and a single, well-chosen task.',
    ingredients: [
      { text: '1 Focus Capsule (or 2, depending on your day)', productId: 'focus-capsules' },
      { text: 'A glass of room-temperature water' },
      { text: 'A notebook and pen' },
      { text: 'A timer set to 25 minutes' }
    ],
    steps: [
      'Take your Focus Capsule with water. Close all tabs, silence notifications.',
      'Write down ONE thing you want to accomplish. Just one.',
      'Set a timer for 25 minutes and begin. No task-switching allowed.',
      'When the timer chimes, close the notebook. Stretch, drink water, look at something far away for a minute.'
    ],
    tip: 'The magic isn\u2019t the herb or the timer \u2014 it\u2019s the promise to yourself that this block is protected.',
    relatedProducts: ['focus-capsules', 'fireball-elixir']
  },
  {
    id: 'immune-boost-elder-tonic',
    title: 'Immune-Boost Elder Tonic',
    subtitle: 'Warm defenses at the first sniffle',
    time: '10 min',
    difficulty: 'Easy',
    moment: 'Anytime',
    image: 'https://images.unsplash.com/photo-1481671703460-040cb8a2d909?w=1200&q=80&auto=format',
    intro: 'A honey-warmed tonic to lean on when the seasons shift or you feel that first tickle in your throat.',
    ingredients: [
      { text: '1 dropper of Boost Elixir', productId: 'boost-elixir' },
      { text: '1 tsp of raw honey' },
      { text: '1 mug of hot water or ginger tea' },
      { text: 'A squeeze of fresh lemon' }
    ],
    steps: [
      'Bring water to a gentle boil, then let it cool for 60 seconds (raw honey doesn\u2019t love true boiling water).',
      'Stir in the honey until it dissolves, then squeeze in the lemon.',
      'Add 1 dropper of Boost Elixir. Stir gently.',
      'Wrap your hands around the mug and sip slowly. Repeat morning and evening at the first sign of feeling off.'
    ],
    tip: 'Pair with 8 hours of sleep and less screen time \u2014 your immune system will thank you twice.',
    relatedProducts: ['boost-elixir', 'respiratory-support-elixir']
  },
  {
    id: 'sunday-self-care-scrub-ritual',
    title: 'Sunday Self-Care Scrub Ritual',
    subtitle: 'A slow, tropical reset',
    time: '20 min',
    difficulty: 'Easy',
    moment: 'Weekend',
    image: 'https://images.unsplash.com/photo-1570194065650-d99fb4bedf0a?w=1200&q=80&auto=format',
    intro: 'A soft, all-in scrub ritual for the day of rest. Coconut, vanilla and warm water \u2014 no rushing, no productivity.',
    ingredients: [
      { text: 'A generous scoop of Coconut & Vanilla Sugar Scrub', productId: 'coconut-vanilla-sugar-body-scrub' },
      { text: 'A warm shower' },
      { text: 'A cotton robe or fluffy towel' },
      { text: 'A calm playlist (bonus points for acoustic)' }
    ],
    steps: [
      'Play your softest playlist. Step into a warm shower and let your skin fully warm through.',
      'Turn off the water briefly. Scoop the scrub into your hands and massage in slow circles from ankles up.',
      'Pay extra love to elbows, knees and the base of your neck.',
      'Rinse in warm water, then finish with a 15-second cool rinse for glow.',
      'Wrap up in cotton. Sit for 5 minutes before doing anything else.'
    ],
    tip: 'Do this ritual once a week for a month \u2014 you\u2019ll feel the softness of your skin AND the softness of your Sundays change.',
    relatedProducts: ['coconut-vanilla-sugar-body-scrub', 'strawberry-delight-sugar-body-scrub', 'soul-skin-starter-kit']
  },
  {
    id: 'the-post-workout-reset',
    title: 'The Post-Workout Reset',
    subtitle: 'Rejuvenate sore muscles in minutes',
    time: '10 min',
    difficulty: 'Easy',
    moment: 'After exercise',
    image: 'https://images.unsplash.com/photo-1544027993-37dbfe43562a?w=1200&q=80&auto=format',
    intro: 'A quick, invigorating massage-oil ritual after a workout \u2014 to bring circulation to tired muscles and reset your nervous system.',
    ingredients: [
      { text: 'A small palmful of Rejuvenating Massage Oil', productId: 'rejuvenating-massage-oil' },
      { text: 'A hot shower' },
      { text: 'A large glass of water with a pinch of sea salt' }
    ],
    steps: [
      'Shower warm and long. Let the heat open up your muscles.',
      'Step out and pat mostly dry. Warm the massage oil between your palms for 10 seconds.',
      'Apply to shoulders, forearms, hamstrings and calves \u2014 anywhere you worked hard.',
      'Use long, upward strokes toward the heart. Breathe deep. Feel the citrus & peppermint clear your head.',
      'Drink your water. Rest for a full minute before rushing back into your day.'
    ],
    tip: 'For deeper recovery, switch to Therapeutic Massage Oil on heavier training days \u2014 the frankincense & myrrh work harder on tight tissue.',
    relatedProducts: ['rejuvenating-massage-oil', 'therapeutic-massage-oil', 'compassion-care-pack']
  },
  {
    id: 'anxiety-first-aid',
    title: 'Anxiety First-Aid',
    subtitle: 'A three-minute reset for hard moments',
    time: '3 min',
    difficulty: 'Easy',
    moment: 'As needed',
    image: 'https://images.unsplash.com/photo-1506126613408-eca07ce68773?w=1200&q=80&auto=format',
    intro: 'When the day tightens around your chest, this small ritual returns you to your body \u2014 fast, grounded, gentle.',
    ingredients: [
      { text: '1 dropper of A-OKAY Elixir', productId: 'a-okay-elixir' },
      { text: 'A quiet spot (a bathroom stall counts)' },
      { text: 'Your two hands' }
    ],
    steps: [
      'Place one dropper of A-OKAY under your tongue. Set a soft mental timer for 3 minutes.',
      'Press one hand to your chest and the other to your belly. Feel both rise and fall.',
      'Breathe in through the nose for 4 counts. Out through the mouth for 6 counts. Do this 6 rounds.',
      'Name 3 things you can see, 2 things you can hear, 1 thing you can touch.',
      'Return to your day \u2014 a little softer, a little more here.'
    ],
    tip: 'Keep A-OKAY in your bag or car so it\u2019s available exactly when the wave rises. Kindness beats willpower every time.',
    relatedProducts: ['a-okay-elixir', 'somnific-capsules']
  },
  {
    id: 'pet-calming-ritual',
    title: 'Pet Calming Ritual',
    subtitle: 'For thunderstorms, travel & vet days',
    time: '10 min',
    difficulty: 'Easy',
    moment: 'As needed',
    image: 'https://images.unsplash.com/photo-1583337130417-3346a1be7dee?w=1200&q=80&auto=format',
    intro: 'A soft, predictable ritual that helps your companion feel safe when the world gets loud.',
    ingredients: [
      { text: '1\u20132 drops of Calming Pet Drops per 10 lbs body weight', productId: 'calming-pet-drops' },
      { text: 'Your pet\u2019s favorite blanket' },
      { text: 'A dim, cozy corner \u2014 away from windows' }
    ],
    steps: [
      'Shake the Calming Pet Drops. Administer the right dose directly into their mouth or mixed into a small treat.',
      'Bring their blanket to a cozy corner of the house away from noise.',
      'Sit with them at their level. Stroke long, slow lines down their back \u2014 not fast pats.',
      'Speak quietly or hum \u2014 they read your calm through your voice more than words.',
      'Give the drops 20\u201330 minutes to settle in. Stay near without hovering.'
    ],
    tip: 'Start dosing 30 minutes before predictable stressors (car rides, fireworks). Your calm nervous system is half the medicine.',
    relatedProducts: ['calming-pet-drops']
  }
];

export const getRecipe = (id) => recipes.find(r => r.id === id);
